/**
 * T278 — member avatar upload helpers (pure, no IO).
 *
 * Avatars are stored in the existing public R2 bucket (Ticket 06) under
 * `public/avatars/<userId>.<ext>` and served through the `/media` gate. The
 * `public/` prefix makes them readable without an entitlement check (a profile
 * photo is not sensitive), and the per-user object key guarantees one member can
 * never address another member's object.
 */

/** Max member avatar size. Larger files are rejected before any R2 write. */
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

/** Accepted avatar MIME types → canonical stored file extension. */
const AVATAR_MIME_EXT: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

/** Every extension an avatar object may have been stored under (for cleanup). */
export const AVATAR_EXTENSIONS = ["png", "jpg", "webp"] as const;

/** `accept` attribute value for the avatar file input. */
export const AVATAR_ACCEPT = "image/png,image/jpeg,image/webp";

export type AvatarValidationError = "INVALID_AVATAR_TYPE" | "AVATAR_TOO_LARGE";

export function validateAvatarUpload(
  contentType: string,
  sizeBytes: number,
): { ok: true; ext: string } | { ok: false; error: AvatarValidationError } {
  const ext = AVATAR_MIME_EXT[contentType];
  if (!ext) return { ok: false, error: "INVALID_AVATAR_TYPE" };
  if (sizeBytes <= 0) return { ok: false, error: "INVALID_AVATAR_TYPE" };
  if (sizeBytes > AVATAR_MAX_BYTES) return { ok: false, error: "AVATAR_TOO_LARGE" };
  return { ok: true, ext };
}

/** Catalog key (public bucket) for a member's avatar object. */
export function avatarCatalogKey(userId: string, ext: string): string {
  return `public/avatars/${userId}.${ext}`;
}
